/**
 * UI-тексты странового хаба (CountryPageContent / countryPage page-module).
 * Только вертикаль-специфика; SEO/мета-строки остаются в namespace `country`,
 * общие сервис-надзаголовки/«Все статьи»/«Популярные вопросы» — в `servicePage`.
 *
 * Eyebrow-надзаголовки секций имеют вид «<Страна>. <раздел>» — поэтому это
 * функции, принимающие уже локализованное имя страны (склонение/локаль делает
 * компонент и передаёт сюда готовое имя).
 */
export const countryPage = {
    en: {
        // Hero
        kicker: 'Country guide',
        heroPrimary: 'Choose a city',
        heroSecondary: 'Trip services',

        // Practical-info mosaic fact labels
        fact: {
            currency: 'Currency',
            season: 'Season',
            language: 'Language',
            driving: 'Driving',
            sockets: 'Sockets',
            timezone: 'Timezone',
        },

        // Subnav anchor labels
        anchor: {
            overview: 'Overview',
            services: 'Services',
            cities: 'Cities',
            map: 'Map',
            airports: 'Getting there',
            faq: 'FAQ',
            articles: 'Articles',
            related: 'Nearby',
        },

        // Section eyebrows: «<Country>. <suffix>»
        eyebrow: {
            overview: (name) => `${name}. Overview`,
            services: (name) => `${name}. Services`,
            cities: (name) => `${name}. Cities`,
            map: (name) => `${name}. Map`,
            airports: (name) => `${name}. Getting there`,
            qa: (name) => `${name}. Questions & answers`,
            resources: (name) => `${name}. Helpful resources`,
            related: (name) => `${name}. Nearby`,
        },

        // 01 · Overview
        overviewTitle: 'The essentials',
        mosaicAria: 'Key facts and currency rate',

        // 02 · Services
        servicesTitle: 'Everything for your trip — in one place',
        servicesLead: 'We curate trusted services in every category — with prices, instructions and promo codes.',

        // 03 · Cities
        citiesTitle: 'Where to go',
        citiesAll: 'All cities',
        citiesLead: 'The country’s main destinations in one place. Open a detailed guide for each city.',

        // 04 · Map
        mapTitle: 'On the map',
        mapLead: 'The country’s cities, attractions and airports — toggle the layers from the legend.',

        // 05 · Getting there
        airportsTitle: 'Airports',
        airportsLead: 'The country’s main airports with IATA codes, names and arrival guides.',
        airportIntl: 'International airport',

        // 06 · FAQ
        faqLead: 'A quick read on visas, currency, season and logistics before your trip.',

        // 07 · Articles
        articlesTitle: 'Articles',
        articlesLead: 'Routes, budgets and practical guides from our editors.',

        // 08 · Related countries
        relatedTitle: 'Similar destinations',
        relatedAll: 'All destinations',
        relatedLead: 'Destinations with a similar spirit — with the same guides and trusted services.',
        relatedTag: 'Destination',
        relatedOpen: 'Open',

        // Bottom CTA
        ctaEyebrow: 'Plan your whole trip in one evening',
        ctaTitle: (name) => `Plan ${name} — from A to Z.`,
        ctaCity: 'Start with choosing a city →',
        ctaEsim: 'Start with eSIM →',
        ctaTransport: 'Start with transport →',
        ctaInsurance: 'Start with insurance →',
    },
    ru: {
        kicker: 'Гайд по стране',
        heroPrimary: 'Выбрать город',
        heroSecondary: 'Сервисы для поездки',

        fact: {
            currency: 'Валюта',
            season: 'Сезон',
            language: 'Язык',
            driving: 'Движение',
            sockets: 'Розетки',
            timezone: 'Часовой пояс',
        },

        anchor: {
            overview: 'О направлении',
            services: 'Сервисы',
            cities: 'Города',
            map: 'Карта',
            airports: 'Как добраться',
            faq: 'Вопросы',
            articles: 'Статьи',
            related: 'Рядом',
        },

        eyebrow: {
            overview: (name) => `${name}. О направлении`,
            services: (name) => `${name}. Сервисы`,
            cities: (name) => `${name}. Города`,
            map: (name) => `${name}. Карта`,
            airports: (name) => `${name}. Как добраться`,
            qa: (name) => `${name}. Вопросы и ответы`,
            resources: (name) => `${name}. Полезные материалы`,
            related: (name) => `${name}. Рядом`,
        },

        overviewTitle: 'Коротко о главном',
        mosaicAria: 'Ключевые факты и курс валюты',

        servicesTitle: 'Всё для поездки — в одном месте',
        servicesLead: 'Мы отобрали проверенные сервисы в каждой категории — с ценами, инструкциями и промокодами.',

        citiesTitle: 'Куда поехать',
        citiesAll: 'Все города',
        citiesLead: 'Главные направления страны в одном месте. Откройте подробный гайд по каждому городу.',

        mapTitle: 'На карте',
        mapLead: 'Города, достопримечательности и аэропорты страны.',

        airportsTitle: 'Аэропорты',
        airportsLead: 'Главные аэропорты страны: коды IATA, названия и подробные гайды по прилёту.',
        airportIntl: 'Международный аэропорт',

        faqLead: 'Коротко о визах, валюте, сезоне и логистике перед поездкой.',

        articlesTitle: 'Статьи',
        articlesLead: 'Маршруты, бюджет и практические гайды от нашей редакции.',

        relatedTitle: 'Похожие направления',
        relatedAll: 'Все направления',
        relatedLead: 'Близкие по духу страны — с такими же гайдами и проверенными сервисами.',
        relatedTag: 'Направление',
        relatedOpen: 'Открыть',

        ctaEyebrow: 'Поездка собирается за один вечер',
        ctaTitle: (name) => `Спланируйте путешествие в ${name} — от А до Я.`,
        ctaCity: 'Начать с выбора города →',
        ctaEsim: 'Начать с eSIM →',
        ctaTransport: 'Начать с транспорта →',
        ctaInsurance: 'Начать со страховки →',
    },
};
